import { XMLParser, XMLValidator } from "fast-xml-parser";

import type { WiEmorInventoryItem, WiEmorReportDefinition } from "./wi-emor-report-definition.types";

export enum WiEmorExampleFileErrorCode {
  InvalidXml = "WI_EMOR_INVALID_XML",
  MissingInventoryId = "WI_EMOR_MISSING_INVENTORY_ID",
  MissingPwsId = "WI_EMOR_MISSING_PWS_ID",
  MissingTreatmentNumber = "WI_EMOR_MISSING_TREATMENT_NUMBER",
  NoInventoryItems = "WI_EMOR_NO_INVENTORY_ITEMS",
  NotEmorSubmission = "WI_EMOR_NOT_EMOR_SUBMISSION",
}

export class WiEmorExampleFileError extends Error {
  public constructor(public readonly code: WiEmorExampleFileErrorCode) {
    super(code);
    this.name = "WiEmorExampleFileError";
  }
}

const ATTRIBUTES = "$attrs";

// the elements that repeat, forced to arrays so a file with one inventory item parses like one with ten
const listPaths = new Set([
  "emorSubmission.facility.inventoryItem",
  "emorSubmission.facility.inventoryItem.fieldData",
]);

const parser = new XMLParser({
  ignoreAttributes: false,
  attributesGroupName: ATTRIBUTES,
  attributeNamePrefix: "",
  parseAttributeValue: false,
  parseTagValue: false,
  trimValues: true,
  ignoreDeclaration: true,
  isArray: (_tagName, jPath) => typeof jPath === "string" && listPaths.has(jPath),
});

type XmlNode = Record<string, unknown>;

const isXmlNode = (value: unknown): value is XmlNode =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * the trimmed attribute of an element, null when the element or the attribute is not there
 */
const attributeOf = (node: unknown, name: string): string | null => {
  if (!isXmlNode(node)) return null;
  const attributes = node[ATTRIBUTES];
  if (!isXmlNode(attributes)) return null;
  const value = attributes[name];
  return typeof value === "string" ? value.trim() : null;
};

const listOf = (node: unknown, name: string): unknown[] => {
  if (!isXmlNode(node)) return [];
  const list = node[name];
  return Array.isArray(list) ? list : [];
};

/**
 * one inventory item with each treatment number once, in the order the file first lists it, the per day rows collapse
 */
const buildInventoryItem = (item: unknown): WiEmorInventoryItem => {
  const inventoryId = attributeOf(item, "inventoryid");
  if (!inventoryId) {
    throw new WiEmorExampleFileError(WiEmorExampleFileErrorCode.MissingInventoryId);
  }

  const treatmentNumbers = new Set<string>();
  for (const fieldData of listOf(item, "fieldData")) {
    const treatmentNumber = attributeOf(fieldData, "treatmentNumber");
    if (!treatmentNumber) {
      throw new WiEmorExampleFileError(WiEmorExampleFileErrorCode.MissingTreatmentNumber);
    }
    treatmentNumbers.add(treatmentNumber);
  }

  return {
    inventoryId,
    inventoryName: attributeOf(item, "inventoryName") ?? "",
    fields: [...treatmentNumbers].map((treatmentNumber) => ({ treatmentNumber })),
  };
};

/**
 * the saved structure of a wisconsin emor report, read off the example file the state hands each system
 *
 * the file is one month of empty fieldData rows, so only its shape is kept: the facility, the inventory items in file order and the treatment numbers each one reports, the dates and values are dropped
 * anything that is not an emor submission with a pws id and at least one inventory item throws a WiEmorExampleFileError
 */
export const parseWiEmorExampleFile = (xml: string): WiEmorReportDefinition => {
  if (XMLValidator.validate(xml) !== true) {
    throw new WiEmorExampleFileError(WiEmorExampleFileErrorCode.InvalidXml);
  }

  const document: unknown = parser.parse(xml);
  if (!isXmlNode(document) || !("emorSubmission" in document)) {
    throw new WiEmorExampleFileError(WiEmorExampleFileErrorCode.NotEmorSubmission);
  }

  // an empty root parses to an empty string, it still is a submission, just one with nothing in it
  const submission = isXmlNode(document.emorSubmission) ? document.emorSubmission : {};
  const facility = submission.facility;
  const pwsId = attributeOf(facility, "pwsID");
  if (!pwsId) {
    throw new WiEmorExampleFileError(WiEmorExampleFileErrorCode.MissingPwsId);
  }

  const inventoryItems = listOf(facility, "inventoryItem").map(buildInventoryItem);
  if (inventoryItems.length === 0) {
    throw new WiEmorExampleFileError(WiEmorExampleFileErrorCode.NoInventoryItems);
  }

  return {
    schemaVersion: attributeOf(submission.metaData, "schemaVersion"),
    pwsId,
    pwsName: attributeOf(facility, "pwsName") ?? "",
    inventoryItems,
  };
};
